import type { MetaFunction, LoaderFunctionArgs } from 'react-router';
import { useLoaderData } from 'react-router';
import ExcelGlory from '~/assets/excel-glory.svg';
import { generateMeta } from '~/services/meta';
import { MicroLandingNew } from '~/components/ProductMicroLanding';
import ExcelHowTo from '~/assets/excel-online.png';

import type { SitemapFunction } from 'remix-sitemap';

const AI_PROVIDERS = {
    claude: {
        name: 'Claude',
        title: 'Claude plugin for Excel',
        subTitle:
            "Integrate Anthropic's Claude AI directly into Excel. Generate formulas, analyze data, and get thoughtful insights without leaving your spreadsheet.",
        metaTitle: 'Claude Add-In for Excel',
        metaDescription:
            'Use Anthropic Claude AI in Excel. Generate formulas, analyze data, and get intelligent insights directly in your spreadsheet.',
        features: [
            "✅ Anthropic's Claude AI for intelligent formula creation",
            '📊 Thoughtful data analysis and insights',
            '🕒 Save hours on spreadsheet work',
            '🔁 Seamless integration within Excel',
        ],
        feedback: {
            message:
                "Claude in Excel provides incredibly thoughtful data analysis. It understands the context of our data and gives us insights we wouldn't have thought of.",
            name: 'Jennifer Martinez',
            role: 'Financial Analyst at DataCorp',
        },
    },
    gemini: {
        name: 'Gemini',
        title: 'Gemini plugin for Excel',
        subTitle:
            "Get Google's powerful Gemini AI directly in Excel. Generate formulas, analyze data, and work smarter with advanced AI assistance.",
        metaTitle: 'Gemini Add-In for Excel',
        metaDescription:
            'Use Google Gemini AI in Excel. Advanced formula generation, data analysis, and spreadsheet automation.',
        features: [
            "✅ Google's advanced Gemini AI integrated in Excel",
            '📊 Superior data analysis capabilities',
            '🕒 Boost productivity with smart AI assistance',
            '🔁 Stay inside Excel — no switching apps',
        ],
        feedback: {
            message:
                'Gemini in Excel has transformed our data workflow. The AI suggestions for formulas and data analysis are incredibly accurate and helpful.',
            name: 'Robert Chen',
            role: 'Business Analyst at TechMetrics',
        },
    },
    deepseek: {
        name: 'DeepSeek',
        title: 'DeepSeek plugin for Excel',
        subTitle:
            "Experience DeepSeek's advanced AI capabilities in Excel. Generate complex formulas, analyze data patterns, and automate spreadsheet tasks.",
        metaTitle: 'DeepSeek Add-In for Excel',
        metaDescription:
            'Use DeepSeek AI in Excel. Advanced formula generation, data analysis, and intelligent spreadsheet automation.',
        features: [
            "✅ DeepSeek's advanced AI for complex calculations",
            '🧠 Superior pattern recognition in data',
            '📊 Advanced data analysis and insights',
            '🔁 Work efficiently without leaving Excel',
        ],
        feedback: {
            message:
                "DeepSeek in Excel understands complex data relationships better than any tool we've used. It creates formulas that perfectly match our needs.",
            name: 'Lisa Wang',
            role: 'Data Scientist at Innovation Labs',
        },
    },
    perplexity: {
        name: 'Perplexity',
        title: 'Perplexity plugin for Excel',
        subTitle:
            "Bring Perplexity's research-powered AI to Excel. Get accurate data insights, formula explanations, and reliable analysis.",
        metaTitle: 'Perplexity Add-In for Excel',
        metaDescription:
            'Use Perplexity AI in Excel. Research-backed data analysis with accurate, sourced insights and formula generation.',
        features: [
            '✅ Research-powered AI for accurate analysis',
            '🔍 Data insights backed by reliable sources',
            '📊 Fact-checked analysis and reporting',
            '🔁 Integrated research capabilities in Excel',
        ],
        feedback: {
            message:
                'Perplexity in Excel is perfect for our research team. The AI provides well-sourced data analysis that we can trust for our reports.',
            name: 'Dr. Sarah Thompson',
            role: 'Research Analyst at Data Institute',
        },
    },
};

export const sitemap: SitemapFunction = () => {
    return Object.keys(AI_PROVIDERS).map((provider) => ({
        loc: `/plugin-for-excel/${provider}`,
        lastmod: new Date().toISOString(),
        exclude: false,
    }));
};

export async function loader({ params }: LoaderFunctionArgs) {
    const integrationName = params.integrationName;

    if (
        !integrationName ||
        !AI_PROVIDERS[integrationName as keyof typeof AI_PROVIDERS]
    ) {
        throw new Response('Not Found', { status: 404 });
    }

    return {
        provider: AI_PROVIDERS[integrationName as keyof typeof AI_PROVIDERS],
    };
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
    if (!data?.provider) {
        return [{ title: 'Not Found' }];
    }

    return generateMeta({
        title: data.provider.metaTitle,
        description: data.provider.metaDescription,
    });
};

export default function PluginExcelPage() {
    const { provider } = useLoaderData<typeof loader>();

    return (
        <MicroLandingNew
            hero={{
                imgUrl: ExcelGlory,
                title: provider.title,
                subTitle: provider.subTitle,
                productName: 'Excel',
                features: provider.features,
                samplePrompts: [
                    'generate-excel-formula',
                    'explain-excel-formula',
                ],
                feedback: provider.feedback,
                install: [
                    {
                        label: 'Install for Excel App',
                        url: '/chatgpt-for-microsoft-excel/install/desktop',
                    },
                    {
                        label: 'Install for Excel Online',
                        url: '/chatgpt-for-microsoft-excel/install/online',
                    },
                ],
            }}
            instruction={{
                title: 'How it works inside Excel?',
                demoEl: (
                    <div
                        style={{
                            position: 'relative',
                            paddingTop: '56.25%',
                        }}>
                        <iframe
                            title={`ChatGPT for Excel`}
                            src={
                                'https://www.youtube-nocookie.com/embed/2qzr_tAIfO0?si=iyeCItkIK3ZIjb7i'
                            }
                            loading='lazy'
                            style={{
                                border: 'none',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                height: '100%',
                                width: '100%',
                            }}
                            allow='accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;'
                            allowFullScreen
                        />
                    </div>
                ),
                steps: [
                    <>
                        <b>Open Excel:</b> Highlight any cell of the spreadsheet
                        in Microsoft Excel document
                    </>,
                    <>
                        <b>Use AI Perfect Assistant:</b> Open AI Perfect
                        Assistant, select any AI Prompt Template and Click
                        "Generate with AI"
                    </>,
                    <>
                        <b>Insert result:</b> Click "Insert" to insert AI
                        generated text to the Excel spreadsheet
                    </>,
                ],
            }}
        />
    );
}
